"use client"

import { useRef } from "react"
import { apiFetch } from "@/lib/api-client"
import { supabase } from "@/lib/supabase/client"
import { useUploadContext } from "@/lib/upload/upload-context"
import { useRouter } from "next/navigation"
import { Toast } from "@/lib/toast"

function getAnonId() {
  let anonId = localStorage.getItem("anon_id")

  if (!anonId) {
    anonId = crypto.randomUUID()
    localStorage.setItem("anon_id", anonId)
  }

  return anonId
}

async function safeJson(res: Response, label: string) {
  if (!res.ok) {
    const text = await res.text()
    console.error(`❌ ${label} ERROR:`, text)
    throw new Error(`${label}_FAILED`)
  }
  return res.json()
}

/**
 * Fetch wrapper that auto-retries on transient server failures (5xx,
 * 408, 429). One retry with a 700ms backoff — enough to shake off a
 * Vercel cold-start + Supabase slow signed-URL generation without
 * making the user wait too long if it's a real outage.
 *
 * Returns the final Response — caller handles the body as usual.
 */
async function withRetry(
  doFetch: () => Promise<Response>,
  label: string,
): Promise<Response> {
  const RETRYABLE = new Set([408, 425, 429, 500, 502, 503, 504])
  let lastRes: Response | null = null

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const res = await doFetch()
      if (res.ok || !RETRYABLE.has(res.status)) return res
      lastRes = res
      if (attempt === 0) {
        console.warn(`⚠️ ${label} transient ${res.status} — retrying…`)
        await new Promise((r) => setTimeout(r, 700))
      }
    } catch (err) {
      // Network error (TypeError / AbortError). Retry once.
      if (attempt === 0) {
        console.warn(`⚠️ ${label} network error — retrying…`, err)
        await new Promise((r) => setTimeout(r, 700))
        continue
      }
      throw err
    }
  }
  return lastRes as Response
}

/** Human message for an HTTP status, so the user sees something
    actionable in the Upload-failed card instead of a generic error. */
function friendlyFailureMessage(label: string, status: number | null): string {
  if (status === 504 || status === 408) {
    return "The server took too long to respond. This is usually a temporary blip — tap Retry."
  }
  if (status === 503 || status === 502) {
    return "Our upload service is taking a break. Try again in a few seconds."
  }
  if (status === 500) {
    return "Something broke on our end preparing your upload. Please retry."
  }
  if (status === 429) {
    return "Too many uploads in a short window. Wait a moment and retry."
  }
  if (label === "SIGNED_URL") {
    return "Couldn't prepare the upload slot. Please retry."
  }
  return "Upload failed. Please try again."
}

export function useUpload() {
  const lock = useRef(false)

  const {
    startUpload,
    setProgress,
    finishUpload,
    failUpload,
  } = useUploadContext()

  const router = useRouter()

  const handleUpload = async (
    file: File,
    options?: { source?: "dashboard" | "hero" | "sample" }
  ) => {
    if (!file) return
    const source = options?.source ?? "dashboard"
    const isSample = source === "sample"

    if (lock.current) {
      Toast.error("Upload already in progress")
      return
    }

    // Lock early — before any async work — so concurrent triggers are blocked
    lock.current = true

    // ── Client-side pre-check: detect missing email column instantly ──
    // Read first 4 KB of the CSV in the browser before uploading.
    // If no email column is found, show the error immediately without
    // starting the upload or showing the progress bar.
    try {
      const snippet = await readFirstLines(file, 2)
      if (!hasEmailColumn(snippet)) {
        Toast.error("We couldn't detect an email column in your file.")
        lock.current = false
        return
      }
    } catch {
      // If we can't read the file, let the server handle it
    }

    try {
      // Fetch the session BEFORE startUpload so we can pass an
      // accurate notice into the global card (no separate toast).
      const {
        data: { session },
      } = await supabase.auth.getSession()

      const anonId = getAnonId()
      const userId = session?.user?.id ?? anonId

      // Build the per-upload notice that lives inside the global
      // status card. Replaces two separate Toast.info calls (sample
      // mode + email-when-done) — one card, one place to look.
      const userEmail = session?.user?.email
      const emailNotificationsEnabled = session?.user?.user_metadata?.email_notifications !== false
      const notice = isSample
        ? "Sample run — no quota consumed"
        : userEmail && emailNotificationsEnabled
          ? `We'll email ${userEmail} when done`
          : undefined

      // 🔥 START
      startUpload(file.name, { notice })
      setProgress(10)

      // 🔥 SIGNED URL — retries once on transient 5xx/timeouts.
      // Supabase storage occasionally needs a moment on cold
      // starts or when the user's folder has lots of objects.
      let resSigned: Response
      try {
        resSigned = await withRetry(
          () => apiFetch("/api/upload-url", {
            method: "POST",
            body: JSON.stringify({ userId }),
          }),
          "SIGNED_URL",
        )
      } catch (err) {
        console.error("❌ SIGNED_URL NETWORK ERROR:", err)
        failUpload("Couldn't reach our servers. Check your connection and retry.")
        lock.current = false
        return
      }

      if (!resSigned.ok) {
        failUpload(friendlyFailureMessage("SIGNED_URL", resSigned.status))
        lock.current = false
        return
      }

      const signed = await resSigned.json()

      setProgress(30)

      // 🔥 UPLOAD to storage — retry once on network failure.
      let uploadRes: Response
      try {
        uploadRes = await withRetry(
          () => fetch(signed.uploadUrl, { method: "PUT", body: file }),
          "STORAGE_PUT",
        )
      } catch (err) {
        console.error("❌ STORAGE_PUT NETWORK ERROR:", err)
        failUpload("Network dropped while uploading the file. Please retry.")
        lock.current = false
        return
      }

      if (!uploadRes.ok) {
        failUpload(friendlyFailureMessage("STORAGE_PUT", uploadRes.status))
        lock.current = false
        return
      }

      setProgress(60)

      // 🔥 CREATE JOB — same retry pattern for the job queue call.
      let res: Response
      try {
        res = await withRetry(
          () => apiFetch("/api/jobs/create", {
            method: "POST",
            body: JSON.stringify({
              filePath: signed.path,
              delimiter: ",",
              originalFileName: file.name,
              userId,
              source,
            }),
          }),
          "JOB_CREATE",
        )
      } catch (err) {
        console.error("❌ JOB_CREATE NETWORK ERROR:", err)
        failUpload("Network dropped while queueing your job. Please retry.")
        lock.current = false
        return
      }

      const data = await res.json()

      if (!res.ok) {
        const code = data?.error || "JOB_CREATE_FAILED"

        const messages: Record<string, string> = {
          TOO_MANY_JOBS:      "A dataset is already processing. Wait for it to finish.",
          JOB_ALREADY_RUNNING:"A dataset is already processing. Wait for it to finish.",
          ROWS_LIMIT_REACHED: "You've used all your rows for this month. Upgrade to continue.",
          NOT_AUTH:           "Your session expired. Please log in again.",
          MISSING_FILE:       "Something went wrong with the upload. Please try again.",
          INVALID_FILE_PATH:  "Invalid file. Please try uploading again.",
          RATE_LIMITED:       "Too many uploads. Please wait a moment before trying again.",
          COOLDOWN:           "Please wait a moment before uploading again.",
        }

        const message = messages[code] ?? "Upload failed. Please try again."

        if (code === "ROWS_LIMIT_REACHED") {
          router.push("/dashboard/billing")
        } else if (code === "TOO_MANY_JOBS" || code === "JOB_ALREADY_RUNNING") {
          router.push("/dashboard/datasets")
        }

        // Single source of truth: the GlobalUploadStatus card carries
        // the message + the Retry button. Previously we ALSO fired a
        // Toast.error here, which double-stacked the same error in two
        // surfaces (top pill + bottom card with retry).
        failUpload(message)
        lock.current = false
        return
      }

      const datasetId = data.datasetId

      // (The "we'll email when done" + "sample run" notices used to
      // live in two separate Toast.info calls here. They've been
      // unified into the global card's `notice` field — see the
      // startUpload() call above.)

      // =========================================
      // 🔥 POLLING — tolerant to transient errors
      // =========================================
      // Tuning:
      //   - 800ms interval
      //   - MAX_ATTEMPTS: ~5 min ceiling for very large files
      //   - MAX_CONSECUTIVE_ERRORS: fail only after this many in a row
      const MAX_ATTEMPTS = 375                // 375 × 800ms = 5 minutes
      const MAX_CONSECUTIVE_ERRORS = 8        // 8 × 800ms = ~6 s of continuous failure
      let attempts = 0
      let consecutiveErrors = 0

      const pollDataset = async () => {
        attempts++

        try {
          const res = await apiFetch(`/api/datasets/${datasetId}/status`)

          // Soft-fail: treat non-2xx as transient unless it's a hard-auth error
          if (!res.ok) {
            if (res.status === 401 || res.status === 403) {
              // Terminal — user lost access / session expired
              failUpload()
              lock.current = false
              return
            }
            consecutiveErrors++
            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
              softEscape("We lost connection while your file was processing")
              return
            }
            setTimeout(pollDataset, 800)
            return
          }

          const status = await res.json()
          consecutiveErrors = 0

          if (status.ready) {
            setProgress(100)
            window.dispatchEvent(new Event("usage-updated"))

            setTimeout(() => {
              finishUpload()
              lock.current = false
              router.push(`/dashboard/datasets/${datasetId}`)
            }, 400)

            return
          }

          // Terminal failure states. Each gets a tailored message and
          // (where it makes sense) a redirect to the dataset detail
          // page so the user sees the appropriate failure card —
          // especially for invalid_csv, where the RepairSection on the
          // detail page is the main path forward.
          if (
            status.status === "cancelled" ||
            status.status === "failed" ||
            status.status === "no_email_column" ||
            status.status === "invalid_csv"
          ) {
            apiFetch("/api/jobs/release-lock", { method: "POST" }).catch(() => {})
            const message =
              status.status === "invalid_csv"
                ? "Your CSV has structural issues. Opening the dataset to show how to repair it…"
              : status.status === "no_email_column"
                ? "We couldn't find an email column in your file."
              : status.status === "cancelled"
                ? "Upload was cancelled."
              : undefined  // generic "failed" → fall back to default
            failUpload(message)
            lock.current = false
            // Route to the dataset detail page so the user sees the
            // failure card (and Auto-repair for invalid_csv).
            // Skip nav for "cancelled" — they already know.
            if (status.status !== "cancelled") {
              router.push(`/dashboard/datasets/${datasetId}`)
            }
            return
          }

          // Still processing — tick the bar forward
          setProgress((p) => Math.min(p + 1.5, 95))

          // Overall timeout — assume it completed in background, redirect gracefully
          if (attempts >= MAX_ATTEMPTS) {
            softEscape("Processing is taking longer than expected")
            return
          }

          setTimeout(pollDataset, 800)

        } catch (err) {
          // Network error — transient. Count it, but don't fail hard.
          console.error("Poll error:", err)
          consecutiveErrors++
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            softEscape("We lost connection while your file was processing")
            return
          }
          setTimeout(pollDataset, 800)
        }
      }

      // Graceful exit: the worker may have finished, but we can't verify from
      // the client. Show a non-error completion and send the user to the
      // datasets list so they can see the final state.
      const softEscape = (note: string) => {
        Toast.info("Moved to background", note + ". Check your datasets list in a moment.")
        window.dispatchEvent(new Event("usage-updated"))
        finishUpload()
        lock.current = false
        router.push(`/dashboard/datasets`)
      }

      pollDataset()

    } catch (err: any) {
      console.error("UPLOAD ERROR:", err)
      const msg = err?.message?.includes("SIGNED_URL")
        ? "Couldn't start the upload. Check your connection and try again."
        : err?.message?.includes("UPLOAD_FAILED")
        ? "File upload failed. Make sure your file is a valid CSV under the size limit."
        : "Something went wrong. Please try again."
      // GlobalUploadStatus carries the message + Retry; no parallel
      // toast (avoids the duplicate top-pill + bottom-card pattern).
      failUpload(msg)
    } finally {
      lock.current = false
    }
  }

  return { handleUpload }
}

// ── Helpers for client-side email column detection ────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Read the first N lines of a File object as plain text. */
function readFirstLines(file: File, n: number): Promise<string> {
  return new Promise((resolve, reject) => {
    // Read only the first 4 KB — more than enough for a CSV header + first row
    const slice = file.slice(0, 4096)
    const reader = new FileReader()
    reader.onload  = (e) => {
      const text  = (e.target?.result as string) ?? ""
      const lines = text.split("\n").slice(0, n).join("\n")
      resolve(lines)
    }
    reader.onerror = reject
    reader.readAsText(slice)
  })
}

/** Return true if the CSV snippet has an email column header or email value. */
function hasEmailColumn(snippet: string): boolean {
  const lines = snippet.split("\n").filter(Boolean)
  if (lines.length === 0) return false

  const headerTokens = lines[0].split(",").map((t) => t.trim().replace(/"/g, "").toLowerCase())
  if (headerTokens.some((t) => t.includes("email"))) return true

  // No header match — check first data row for email-shaped values
  const dataTokens = (lines[1] ?? "").split(",").map((t) => t.trim().replace(/"/g, ""))
  return dataTokens.some((t) => EMAIL_RE.test(t))
}